import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Top3.css";
import "../App.css";
import axios from "axios";
import OverallScoreTooltip from "../components/OverallScoreTooltip";

/* =======================
   Helpers / Utils (DRY)
   ======================= */
const isEmpty = (v) => v === null || v === undefined || v === "" || v === false;

const safeFixed = (value, decimals = 1) =>
  typeof value === "number" && !Number.isNaN(value)
    ? Number(value.toFixed(decimals))
    : null;

const formatCurrency = (value) =>
  typeof value === "number"
    ? value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 0,
      })
    : "—";

const getColor = (value) =>
  value >= 8 ? "#4CAF50" : value >= 5 ? "#FFC107" : "#F44336";

const isPriceFilter = (f) => {
  const txt =
    `${f.key || ""} ${f.keyMin || ""} ${f.keyMax || ""} ${f.label || ""}`.toLowerCase();
  return (
    txt.includes("price") || txt.includes("preço") || txt.includes("preco")
  );
};

const isWeightFilter = (f) => {
  const txt = `${f.key || ""} ${f.label || ""}`.toLowerCase();
  return txt.includes("weight") || txt.includes("peso");
};

const fmtSliderVal = (f, v) => {
  if (v == null) return "";

  const key = f.key?.toLowerCase() || "";

  if (isPriceFilter(f)) return formatCurrency(Number(v));
  if (isWeightFilter(f)) return `${Number(v)} kg`;

  // 🔹 Tamanho de tela (polegadas)
  if (key === "screendisplaysize") {
    return `${Number(v)}"`;
  }

  // 🔹 Taxa de atualização (Hz)
  if (key === "screenrefreshrate") {
    return `${Number(v)} Hz`;
  }

  return String(v);
};

const getSliderBackground = (min, max, valueMin, valueMax, active) => {
  if (!active) return `linear-gradient(to right, #444 0%, #444 100%)`;
  const vMin = Number(valueMin);
  const vMax = Number(valueMax);
  const percentMin = ((vMin - min) / (max - min)) * 100;
  const percentMax = ((vMax - min) / (max - min)) * 100;
  return `linear-gradient(
    to right,
    #444 ${percentMin}%,
    var(--accent-orange) ${percentMin}%,
    var(--accent-orange) ${percentMax}%,
    #444 ${percentMax}%
  )`;
};

const normalizeParamsFromURL = (search, filtersConfig = []) => {
  const params = new URLSearchParams(search);
  const initial = {};

  for (const [key, value] of params.entries()) {
    // converte booleanos
    if (value === "true" || value === "false") {
      const boolVal = value === "true";
      if (initial[key]) {
        initial[key] = Array.isArray(initial[key])
          ? [...initial[key], boolVal]
          : [initial[key], boolVal];
      } else {
        initial[key] = boolVal;
      }
      continue;
    }

    // tenta número
    const maybeNum = Number(value);
    const parsedVal =
      !Number.isNaN(maybeNum) && value.trim() !== "" ? maybeNum : value;

    if (initial[key]) {
      initial[key] = Array.isArray(initial[key])
        ? [...initial[key], parsedVal]
        : [initial[key], parsedVal];
    } else {
      initial[key] = parsedVal;
    }
  }

  // 🔹 só converte em array se o filtro for multipleChoice
  const multiKeys = filtersConfig
    .flatMap((g) => g.filters || [])
    .filter((f) => f.type === "multipleChoice")
    .map((f) => f.key);

  Object.keys(initial).forEach((k) => {
    if (multiKeys.includes(k) && !Array.isArray(initial[k])) {
      initial[k] = [initial[k]];
    }
  });

  return initial;
};

const buildQueryStringFromFilters = (activeFilters) => {
  const params = new URLSearchParams();
  Object.entries(activeFilters).forEach(([key, value]) => {
    if (!isEmpty(value)) {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v)); // ✅ envia lista como ?brand=Dell&brand=Asus
      } else {
        params.set(key, value);
      }
    }
  });
  return params.toString() ? `?${params.toString()}` : "";
};

const validateRequired = (filtersConfig, activeFilters) => {
  const errors = {};
  let firstInvalidKey = null;

  filtersConfig.forEach((group) => {
    (group.filters || []).forEach((f) => {
      if (f.required) {
        const val =
          f.type === "slider"
            ? (activeFilters[f.keyMin] ?? activeFilters[f.keyMax])
            : activeFilters[f.key];
        if (isEmpty(val)) {
          errors[f.key] = "O campo é obrigatório.";
          if (!firstInvalidKey) firstInvalidKey = f.key;
        }
      }
    });
  });

  return { errors, firstInvalidKey };
};

const scoreCategoriesFromProduct = (p) =>
  Object.entries(p)
    .filter(
      ([key, value]) =>
        key.endsWith("Score") &&
        value &&
        typeof value === "object" &&
        value.componentScore != null
    )
    .map(([_, score]) => ({
      label: score.label,
      value: safeFixed(score.componentScore),
    }));

/* =======================
   Subcomponent: FilterField
   ======================= */
function FilterField({
  filter,
  errors,
  activeFilters,
  setActiveFilters,
  getOptionsForFilter,
  filterRefs,
}) {
  const [mcShowAll, setMcShowAll] = useState(false);

  const hasValue =
    filter.type === "slider"
      ? activeFilters[filter.keyMin] != null ||
        activeFilters[filter.keyMax] != null
      : !isEmpty(activeFilters[filter.key]);

  const clearThis = () =>
    setActiveFilters((prev) => {
      const next = { ...prev };
      if (filter.type === "slider") {
        delete next[filter.keyMin];
        delete next[filter.keyMax];
      } else {
        delete next[filter.key];
      }
      return next;
    });

  // 🔹 cada tipo desenha sua própria estrutura visual
  switch (filter.type) {
    case "radioButton":
      return (
        <div
          ref={(el) => (filterRefs.current[filter.key] = el)}
          className={`filter-radio-button ${
            errors[filter.key] ? "has-error" : ""
          }`}
        >
          <div className="filter-header">
            <label className="filter-label">
              {filter.label}
              {filter.required && <span className="required-asterisk">*</span>}
            </label>

            {hasValue && (
              <button className="clear-single-filter-btn" onClick={clearThis}>
                ✖
              </button>
            )}
          </div>

          {getOptionsForFilter(filter)
            .slice(0, filter.showAll ? undefined : 5)
            .map((opt) => (
              <label key={opt.value} className="radio-option">
                <input
                  type="radio"
                  name={filter.key}
                  value={opt.value}
                  checked={activeFilters[filter.key] === opt.value}
                  onChange={() =>
                    setActiveFilters((prev) => ({
                      ...prev,
                      [filter.key]: opt.value,
                    }))
                  }
                />
                <span className="custom-radio" />
                {opt.label}
              </label>
            ))}

          {errors[filter.key] && (
            <div className="filter-error">{errors[filter.key]}</div>
          )}
        </div>
      );

    case "slider":
      const valueMin = activeFilters[filter.keyMin] ?? filter.minValue ?? 0;
      const valueMax = activeFilters[filter.keyMax] ?? filter.maxValue ?? 100;
      const min = filter.minValue ?? 0;
      const max = filter.maxValue ?? 100;
      const increment = filter.increment ?? 1;

      const isActive =
        activeFilters[filter.keyMin] != null ||
        activeFilters[filter.keyMax] != null;

      const handleSliderChange = (key, val) =>
        setActiveFilters((prev) => ({
          ...prev,
          [key]: val,
        }));

      return (
        <div
          ref={(el) => (filterRefs.current[filter.key] = el)}
          className={`filter-slider ${errors[filter.key] ? "has-error" : ""}`}
        >
          <div className="filter-header">
            <label className="filter-label">
              {filter.label}
              {filter.required && <span className="required-asterisk">*</span>}
            </label>
            {hasValue && (
              <button className="clear-single-filter-btn" onClick={clearThis}>
                ✖
              </button>
            )}
          </div>

          <div className="range-slider">
            {/* Track e sliders */}
            <div
              className="range-track"
              style={{
                background: getSliderBackground(
                  min,
                  max,
                  valueMin,
                  valueMax,
                  isActive
                ),
              }}
            >
              {/* Slider mínimo */}
              <input
                type="range"
                min={min}
                max={max}
                step={increment}
                value={valueMin}
                onChange={(e) =>
                  handleSliderChange(filter.keyMin, Number(e.target.value))
                }
                style={{ zIndex: valueMin > max - 100 ? 5 : undefined }}
              />

              {/* Slider máximo */}
              <input
                type="range"
                min={min}
                max={max}
                step={increment}
                value={valueMax}
                onChange={(e) =>
                  handleSliderChange(filter.keyMax, Number(e.target.value))
                }
              />
            </div>

            {/* Valores visuais */}
            <div className="range-values">
              <span>{fmtSliderVal(filter, valueMin)}</span>
              <span>{fmtSliderVal(filter, valueMax)}</span>
            </div>
          </div>

          {errors[filter.key] && (
            <div className="filter-error">{errors[filter.key]}</div>
          )}
        </div>
      );

    case "singleCheck":
      return (
        <div
          ref={(el) => (filterRefs.current[filter.key] = el)}
          className="filter-single-check"
        >
          <label className="single-check-item">
            <input
              type="checkbox"
              checked={!!activeFilters[filter.key]}
              onChange={(e) =>
                setActiveFilters((prev) => ({
                  ...prev,
                  [filter.key]: e.target.checked,
                }))
              }
            />
            <span>{filter.label}</span>
          </label>
        </div>
      );

    case "multipleChoice": {
      const selectedValues = Array.isArray(activeFilters[filter.key])
        ? activeFilters[filter.key]
        : [];

      const toggleOption = (val) => {
        setActiveFilters((prev) => {
          const prevVals = Array.isArray(prev[filter.key])
            ? prev[filter.key]
            : [];
          const isSelected = prevVals.includes(val);
          const nextVals = isSelected
            ? prevVals.filter((v) => v !== val)
            : [...prevVals, val];
          return { ...prev, [filter.key]: nextVals };
        });
      };

      const options = getOptionsForFilter(filter);
      const visibleOptions =
        mcShowAll || options.length <= 5 ? options : options.slice(0, 5);

      return (
        <div
          ref={(el) => (filterRefs.current[filter.key] = el)}
          className={`filter-multiple-choice ${errors[filter.key] ? "has-error" : ""}`}
        >
          <div className="filter-header">
            <label className="filter-label">
              {filter.label}
              {filter.required && <span className="required-asterisk">*</span>}
            </label>

            {selectedValues.length > 0 && (
              <button className="clear-single-filter-btn" onClick={clearThis}>
                ✖
              </button>
            )}
          </div>

          {visibleOptions.map((opt) => {
            const checked = selectedValues.includes(opt.value);
            return (
              <label key={opt.value} className="checkbox-option">
                <input
                  type="checkbox"
                  value={opt.value}
                  checked={checked}
                  onChange={() => toggleOption(opt.value)}
                />
                <span className="custom-checkbox" />
                {opt.label}
              </label>
            );
          })}

          {options.length > 5 && (
            <button
              type="button"
              className="see-more-link"
              onClick={() => setMcShowAll((s) => !s)}
            >
              {mcShowAll ? "Ver menos" : `Ver mais (${options.length - 5})`}
            </button>
          )}

          {errors[filter.key] && (
            <div className="filter-error">{errors[filter.key]}</div>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}

/* =======================
   Main Component
   ======================= */
function Top3() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notebooks, setNotebooks] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filtersConfig, setFiltersConfig] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [errors, setErrors] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [hasValidationError, setHasValidationError] = useState(false);
  const [sortings, setSortings] = useState([]);
  const [isSortingOpen, setIsSortingOpen] = useState(false);
  const [selectedSorting, setSelectedSorting] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const sortingRef = useRef(null);
  const [overallScoresWeights, setOverallScoresWeights] = useState({});
  const [tooltipProductId, setTooltipProductId] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const purposeFromQuery = queryParams.get("purpose");
  const [filtersRenderKey, setFiltersRenderKey] = useState(0);

  const hasActiveFilters = useMemo(
    () => Object.values(activeFilters).some((val) => !isEmpty(val)),
    [activeFilters]
  );

  // Carrega configuração de filtros
  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/v1/interface-configuration`
      )
      .then((res) => {
        const data = res.data?.drawerFilters || [];
        const sorts = res.data?.sortings || [];
        setFiltersConfig(data);
        setSortings(sorts);
        setOverallScoresWeights(res.data?.overallScoresWeights || {});

        const initialOpen = {};
        data.forEach((group) => {
          initialOpen[group.label] = false;
        });
        setOpenGroups(initialOpen);
      })
      .catch((err) => console.error("Erro carregando filtros:", err));
  }, []);

  // Validação em tempo real (limpa quando ok)
  useEffect(() => {
    if (filtersConfig.length === 0) return;
    const { errors: newErrors } = validateRequired(
      filtersConfig,
      activeFilters
    );
    if (Object.keys(newErrors).length === 0) {
      setErrors({});
      setHasValidationError(false);
    }
  }, [activeFilters, filtersConfig]);

  useEffect(() => {
    if (!isDrawerOpen) {
      setFiltersRenderKey((k) => k + 1);
    }
  }, [isDrawerOpen]);

  // Fecha o menu "Ordenar" ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortingRef.current && !sortingRef.current.contains(event.target)) {
        setIsSortingOpen(false);
      }
    };

    if (isSortingOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortingOpen]);

  // Fecha o tooltip ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => setTooltipProductId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Mapa id -> filter para dependências
  const filtersById = useMemo(() => {
    const map = new Map();
    filtersConfig.forEach((g) =>
      (g.filters || []).forEach((f) => map.set(f.id, f))
    );
    return map;
  }, [filtersConfig]);

  // Mostrar/ocultar filtro por dependência
  const shouldShowFilter = useCallback(
    (filter) => {
      const req = filter.requiresFilterAnswer;
      if (!req) return true;
      return Object.entries(req).every(([depIdStr, requiredValue]) => {
        const dep = filtersById.get(Number(depIdStr));
        if (!dep) return false;
        const val = activeFilters[dep.key];
        return String(val) === String(requiredValue);
      });
    },
    [filtersById, activeFilters]
  );

  // Busca notebooks
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams(location.search);
    params.set("pageNumber", pageNumber);
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/v1/products?${params.toString()}`;

    axios
      .get(url)
      .then((res) => {
        setNotebooks(res.data?.content || []);
        const pageInfo = res.data?.page || {};
        setTotalPages(pageInfo.totalPages ?? 0);
        setPageNumber(pageInfo.number ?? 0);
      })

      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [location.search, pageNumber]);

  // Purpose efetivo
  const effectivePurpose =
    activeFilters?.purpose ?? purposeFromQuery ?? undefined;

  // Limpa filtros que exigem um purpose específico quando ele muda
  useEffect(() => {
    if (!effectivePurpose) return;
    setActiveFilters((prev) => {
      const next = { ...prev };
      filtersConfig.forEach((group) => {
        (group.filters || []).forEach((filter) => {
          if (filter.requiresFilterAnswer) {
            const requiredPurpose = filter.requiresFilterAnswer["1"];
            if (requiredPurpose && requiredPurpose !== effectivePurpose) {
              delete next[filter.key];
            }
          }
        });
      });
      return next;
    });
  }, [effectivePurpose, filtersConfig]);

  // Lê filtros da URL (mantém multipleChoice como array)
  useEffect(() => {
    if (filtersConfig.length > 0) {
      setActiveFilters(normalizeParamsFromURL(location.search, filtersConfig));
    }
  }, [location.search, filtersConfig]);

  // Refs para scroll até o primeiro inválido
  const filterRefs = useRef({});

  const getOptionsForFilter = useCallback(
    (f) => {
      // 1️⃣ Se tiver opções estáticas
      if (Array.isArray(f.options) && f.options.length) {
        return f.options;
      }

      // 2️⃣ Se tiver dynamicOptions (depende de outro filtro)
      if (f.dynamicOptions) {
        const parentVal = activeFilters[f.dependsOn];

        // 🔹 Função auxiliar para deduplicar mantendo a ordem original
        const dedupPreservandoOrdem = (list) => {
          const seen = new Set();
          const unique = [];
          for (const item of list) {
            const val = String(item.value).trim().toLowerCase();
            if (!seen.has(val)) {
              seen.add(val);
              unique.push(item);
            }
          }
          return unique;
        };

        // ✅ Caso 1: nenhum valor no pai → junta todos os buckets
        if (
          !parentVal ||
          (Array.isArray(parentVal) && parentVal.length === 0)
        ) {
          const all = Object.values(f.dynamicOptions).flat();
          const dedup = dedupPreservandoOrdem(all);
          // opcional: ordena alfabeticamente para consistência
          return dedup.sort((a, b) =>
            a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
          );
        }

        // ✅ Caso 2: o pai tem múltiplos valores (ex: várias marcas)
        if (Array.isArray(parentVal)) {
          const combined = parentVal
            .flatMap((p) => f.dynamicOptions[p] || [])
            .filter(Boolean);
          return dedupPreservandoOrdem(combined);
        }

        // ✅ Caso 3: o pai tem um único valor
        const list = Array.isArray(f.dynamicOptions[parentVal])
          ? f.dynamicOptions[parentVal]
          : [];
        return dedupPreservandoOrdem(list);
      }

      return [];
    },
    [activeFilters]
  );

  // Aplicar filtros (validação única)
  const applyFilters = useCallback(() => {
    const { errors: newErrors, firstInvalidKey } = validateRequired(
      filtersConfig,
      activeFilters
    );

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setHasValidationError(true);

      if (firstInvalidKey) {
        // Descobre em qual grupo o filtro inválido está
        const groupWithError = filtersConfig.find((group) =>
          (group.filters || []).some((f) => f.key === firstInvalidKey)
        );

        if (groupWithError) {
          // Garante que o grupo esteja aberto
          setOpenGroups((prev) => ({
            ...prev,
            [groupWithError.label]: true,
          }));
        }

        // Depois de abrir, dá um pequeno delay e faz scroll
        setTimeout(() => {
          if (filterRefs.current[firstInvalidKey]) {
            filterRefs.current[firstInvalidKey].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      }

      return;
    }

    setErrors({});
    setHasValidationError(false);

    const query = buildQueryStringFromFilters({
      ...activeFilters,
      pageNumber: 0,
    });
    navigate({ pathname: "/top3", search: query });
    setPageNumber(0);
    setIsDrawerOpen(false);
  }, [filtersConfig, activeFilters, navigate]);

  // Chips dos filtros ativos
  const buildChipLabel = useCallback(
    (f) => {
      if (f.type === "radioButton") {
        const val = activeFilters[f.key];
        if (isEmpty(val)) return null;
        const opt = getOptionsForFilter(f).find(
          (o) => String(o.value) === String(val)
        );
        return opt ? opt.label : String(val);
      }
      if (f.type === "singleCheck") {
        if (activeFilters[f.key] !== true) return null;
        return f.label;
      }
      if (f.type === "slider") {
        const minVal = activeFilters[f.keyMin];
        const maxVal = activeFilters[f.keyMax];
        if (minVal == null && maxVal == null) return null;
        if (minVal != null && maxVal == null)
          return `> ${fmtSliderVal(f, minVal)}`;
        if (maxVal != null && minVal == null)
          return `< ${fmtSliderVal(f, maxVal)}`;
        return `${fmtSliderVal(f, minVal)} - ${fmtSliderVal(f, maxVal)}`;
      }
      if (f.type === "multipleChoice") {
        let vals = activeFilters[f.key];
        if (isEmpty(vals)) return null;

        // 🔹 Garante que sempre seja array, mesmo se vier string única da URL
        if (!Array.isArray(vals)) vals = [vals];

        const options = getOptionsForFilter(f);
        const labels = vals
          .map((v) => {
            const match = options.find((o) => String(o.value) === String(v));
            if (match?.label) return match.label;
            // 🔸 fallback
            return v.toString().charAt(0).toUpperCase() + v.toString().slice(1);
          })
          .join(", ");

        return labels;
      }

      if (f.key === "pageNumber") return null;

      return null;
    },
    [activeFilters, getOptionsForFilter]
  );

  // Navega para detalhes
  const handleImageClick = (productId) => {
    const purpose = effectivePurpose || "generic";
    navigate(`/v1/products/${productId}/${purpose}`);
  };

  const handleChipClick = (filterKey) => {
    // Abre o drawer
    setIsDrawerOpen(true);

    // Garante render mínimo antes de interagir
    setTimeout(() => {
      // Descobre o grupo onde está o filtro
      const group = filtersConfig.find((g) =>
        (g.filters || []).some((f) => f.key === filterKey)
      );

      if (group) {
        // Abre imediatamente a seção
        setOpenGroups((prev) => ({
          ...prev,
          [group.label]: true,
        }));
      }

      // Scroll quase imediato após abrir
      requestAnimationFrame(() => {
        const el = filterRefs.current[filterKey];
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Destaque rápido
          el.classList.add("highlighted-filter");
          setTimeout(() => el.classList.remove("highlighted-filter"), 1200);
        }
      });
    }, 100); // ⏱️ antes era 300 + 300 → agora só 100 ms
  };

  // 🔹 Mapeia propósitos internos → labels do backend
  const purposeLabelMap = {
    generic: "Uso geral",
    gamer: "Gamer",
    editing: "Edição de imagem e video",
  };

  // 🔹 Determina o label correto para o propósito atual
  const matchedPurposeLabel =
    purposeLabelMap[effectivePurpose] ||
    Object.keys(overallScoresWeights || {})[0];

  /* =======================
     Render
     ======================= */
  return (
    <div className="main-container-dark">
      {/* 🔹 Barra com botões */}
      {/* 🔹 Header unificado */}
      {/* Header principal */}
      <div className="top-header-wrapper">
        {/* Logo posicionada sobre o canto esquerdo */}
        <div className="top-header-logo-container">
          <a href="/">
            <img
              src={require("../assets/mat-logo.png")}
              alt="Meu Assessor Tech"
              className="top-header-logo"
            />
          </a>
        </div>

        {/* Faixa com botões centralizados */}
        <div className="top-header-dark">
          <div className="top-header-center">
            <button
              className="top-header-btn"
              onClick={() => setIsDrawerOpen(true)}
            >
              ⚙️ Filtros
            </button>

            <div className="sorting-wrapper" ref={sortingRef}>
              <button
                className="top-header-btn"
                onClick={() => setIsSortingOpen((prev) => !prev)}
              >
                Ordenar
              </button>

              {isSortingOpen && (
                <div className="sorting-dropdown">
                  {sortings.map((sort) => (
                    <button
                      key={sort.value}
                      className="sorting-option"
                      onClick={() => {
                        setSelectedSorting(sort.value);
                        setIsSortingOpen(false);

                        const params = new URLSearchParams(location.search);
                        params.set("sorting", sort.value);
                        navigate({
                          pathname: "/top3",
                          search: `?${params.toString()}`,
                        });
                      }}
                    >
                      {sort.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button className="top-header-btn-cta" onClick={applyFilters}>
              Filtrar
            </button>
          </div>
        </div>
      </div>

      {/* 🔹 Resto da página */}
      {hasValidationError && (
        <div className="validation-warning">
          ⚠️ Preencha os filtros obrigatórios.
        </div>
      )}

      {(hasActiveFilters || selectedSorting) && (
        <div className="active-filters-container-inline">
          {filtersConfig
            .flatMap((g) => g.filters || [])
            .map((f) => {
              const label = buildChipLabel(f);
              if (!label) return null;
              return (
                <div key={`chip-${f.id}`} className="active-filter-chip">
                  <span
                    className="chip-label-clickable"
                    onClick={() => handleChipClick(f.key)}
                    title="Ir para este filtro"
                  >
                    {label}
                  </span>

                  <button
                    className="remove-chip-btn"
                    onClick={() =>
                      setActiveFilters((prev) => {
                        const next = { ...prev };
                        if (f.type === "slider") {
                          delete next[f.keyMin];
                          delete next[f.keyMax];
                        } else {
                          delete next[f.key];
                        }
                        return next;
                      })
                    }
                  >
                    ✖
                  </button>
                </div>
              );
            })}

          {selectedSorting && (
            <div className="active-filter-chip">
              <span>
                {sortings.find((s) => s.value === selectedSorting)?.label ||
                  "Ordenação"}
              </span>
              <button
                className="remove-chip-btn"
                title="Remover ordenação"
                onClick={() => {
                  setSelectedSorting(null);
                  const params = new URLSearchParams(location.search);
                  params.delete("sorting"); // ✅ nome correto do parâmetro
                  navigate({
                    pathname: "/top3",
                    search: params.toString() ? `?${params.toString()}` : "",
                  });
                }}
              >
                ✖
              </button>
            </div>
          )}
        </div>
      )}

      {/* Drawer */}
      <div
        className={`drawer-overlay ${isDrawerOpen ? "show" : ""}`}
        onClick={() => setIsDrawerOpen(false)}
      >
        <div
          className={`drawer ${isDrawerOpen ? "open" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="drawer-header">
            <h3>Filtros</h3>
            <button
              className="close-drawer-btn"
              onClick={() => setIsDrawerOpen(false)}
            >
              ✖
            </button>
          </div>

          <div className="drawer-content">
            {filtersConfig.map((group, gi) => (
              <div key={gi} className="filter-section">
                <div
                  className={`filter-section-header ${openGroups[group.label] ? "open" : ""}`}
                  onClick={() =>
                    setOpenGroups((prev) => ({
                      ...prev,
                      [group.label]: !prev[group.label],
                    }))
                  }
                >
                  <h4>{group.label}</h4>
                  <span className="chevron">▼</span>
                </div>

                <div
                  className={`filter-section-body-wrapper ${
                    openGroups[group.label] ? "open" : ""
                  }`}
                >
                  <div className="filter-section-body">
                    {(group.filters || [])
                      .filter((f) => shouldShowFilter(f))
                      .map((f) => (
                        <FilterField
                          key={`ff-${filtersRenderKey}-${f.id}`} // 👈 força remount quando o drawer fecha
                          filter={f}
                          errors={errors}
                          activeFilters={activeFilters}
                          setActiveFilters={setActiveFilters}
                          getOptionsForFilter={getOptionsForFilter}
                          filterRefs={filterRefs}
                          filtersConfig={filtersConfig}
                        />
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="drawer-footer">
            <button
              className="clear-filters-btn"
              onClick={() => {
                setActiveFilters({});
                navigate("/top3");
              }}
            >
              Limpar todos
            </button>
            <button className="apply-filters-btn" onClick={applyFilters}>
              Filtrar
            </button>
          </div>
        </div>
      </div>

      {/* Resultados */}
      {loading ? (
        <div className="loading-indicator-dark">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      ) : notebooks.length === 0 ? (
        <p className="no-results-dark">
          Nenhum notebook encontrado, tente mudar os filtros aplicados
        </p>
      ) : (
        <div className="top3-container-dark">
          <div className="col-12 mb-4">
            <div className="notebook-cards-container-dark">
              {notebooks.map((p, i) => {
                const scoreCategories = scoreCategoriesFromProduct(p);
                return (
                  <div key={p.id} className="notebook-card-dark">
                    {/* Imagem com ranking e nota sobrepostos */}
                    <div className="notebook-image-wrapper-dark">
                      <img
                        src={p.pictures?.[0] || "/no-image.png"}
                        alt={p.title || "Notebook sem nome"}
                        className="notebook-image-dark"
                      />

                      {safeFixed(p.overallScore) != null && (
                        <div className="overall-score-container-dark">
                          <span className="star-icon">★</span>
                          <span
                            className="overall-score-value tooltip-trigger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTooltipProductId((prev) =>
                                prev === p.id ? null : p.id
                              );
                            }}
                          >
                            {`${safeFixed(p.overallScore)}/10`}
                          </span>

                          <OverallScoreTooltip
                            visible={tooltipProductId === p.id}
                            purposeLabel={matchedPurposeLabel}
                            weights={
                              overallScoresWeights[matchedPurposeLabel] || {}
                            }
                            onClose={() => setTooltipProductId(null)}
                          />
                        </div>
                      )}
                    </div>

                    <div className="notebook-content-dark">
                      <h3 className="notebook-title-dark">
                        {p.title || "Notebook sem nome"}
                      </h3>
                      {p.weight && (
                        <div className="notebook-weight-dark">
                          Peso: {p.weight.toFixed(2)} kg
                        </div>
                      )}
                      <div className="notebook-price-dark">
                        <span className="price-value">
                          {formatCurrency(p.price)}
                        </span>
                        <span
                          className="price-info-icon"
                          data-tooltip="Preços mudam com frequência"
                        >
                          ⓘ
                        </span>
                      </div>

                      <div className="score-container-dark">
                        {scoreCategories.map((category, idx) => (
                          <div key={idx} className="score-row-dark">
                            <span className="score-label-dark">
                              {category.label}
                            </span>
                            <div className="score-inline-bar">
                              <div
                                className="score-bar"
                                style={{
                                  width: `${(category.value / 10) * 100}%`,
                                  backgroundColor: getColor(category.value),
                                  opacity: 0.85,
                                }}
                              />
                            </div>

                            <span
                              className="score-value-dark"
                              style={{ color: getColor(category.value) }}
                            >
                              {category.value}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="card-buttons-row">
                        <button
                          className="more-detail-button-dark"
                          onClick={() => handleImageClick(p.id)}
                        >
                          Saiba mais
                        </button>

                        {p.affiliatedLink ? (
                          <a
                            href={p.affiliatedLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cta-button-dark"
                          >
                            Ver oferta
                          </a>
                        ) : (
                          <button className="cta-button-dark disabled" disabled>
                            Oferta indisponível
                          </button>
                        )}
                      </div>
                    </div>

                    {p.marketplaceName && (
                      <div className="marketplace-info-dark">
                        Oferta em {p.marketplaceName}
                      </div>
                    )}
                  </div>
                );
              })}

              {totalPages > 1 && (
                <div className="pagination-dark">
                  {" "}
                  <button
                    className="pagination-btn"
                    disabled={pageNumber <= 0}
                    onClick={() =>
                      setPageNumber((prev) => Math.max(prev - 1, 0))
                    }
                  >
                    {" "}
                    ◀ Anterior{" "}
                  </button>{" "}
                  <span className="pagination-info">
                    {" "}
                    Página {pageNumber + 1} de {totalPages}{" "}
                  </span>{" "}
                  <button
                    className="pagination-btn"
                    disabled={pageNumber >= totalPages - 1}
                    onClick={() => setPageNumber((prev) => prev + 1)}
                  >
                    {" "}
                    Próxima ▶{" "}
                  </button>{" "}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Top3;
